import * as Sentry from "@sentry/nextjs"
import { Button, Modal, Spinner, TextInput } from "flowbite-react"
import { Form, Formik, FormikHelpers } from "formik"
import React, { useMemo } from "react"
import * as Yup from "yup"

import FormGroup from "@components/elements/FormGroup"
import PhoneInput from "@components/elements/PhoneInput"
import useToast from "@hooks/useToast"
import * as CustomerService from "@mono/essentials/services/CustomerService"
import * as OrderServices from "@mono/essentials/services/OrderServices"

import { CustomerDetailsFormAttributes } from "../CustomerDetailsForm"

interface EditCustomerModalProps {
  show: boolean
  handleClose: () => void
  onSuccess?: (customerId?: string) => void
  customerData?: any
  isIntegratedPage?: boolean
}

const EditCustomerModal = ({
  show,
  handleClose,
  onSuccess,
  customerData,
  isIntegratedPage,
}: EditCustomerModalProps) => {
  const { showToast } = useToast()

  const initialValues: CustomerDetailsFormAttributes = useMemo(
    () => ({
      first_name: customerData?.first_name ?? "",
      last_name: customerData?.last_name ?? "",
      email: customerData?.email ?? "",
      phone: customerData?.phone ?? "",
      phone_code: customerData?.phone_code ?? "",
    }),
    [customerData]
  )

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        first_name: Yup.string().required("*First Name is required"),
        last_name: Yup.string().required("*Last Name is required"),
        email: Yup.string()
          .email(`Please enter your account's email`)
          .required("*Please enter your email"),
        phone: Yup.string()
          .required("*Phone Number is required")
          .min(10, "Phone number must be at least 10 characters")
          .max(13, "Phone number cannot exceed 13 characters"),
      }),
    []
  )

  const onSubmit = async (
    values: CustomerDetailsFormAttributes,
    { setSubmitting, setFieldError, resetForm }: FormikHelpers<CustomerDetailsFormAttributes>
  ) => {
    try {
      setSubmitting(true)
      const payload: any = {
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email,
        phone: values.phone,
        phone_code: values.phone_code,
        status: "active",
      }
      const { data } = await CustomerService.updateCustomer(
        customerData?.id,
        payload,
        isIntegratedPage
      )
      onSuccess?.()
    } catch (error: any) {
      Sentry.captureException(error)
      console.error({ error })
      showToast({ type: "danger", heading: "Something went wrong! Please try again later" })
      const errors = error?.response?.data?.errors || []
      for (const key of Object.keys(errors)) {
        setFieldError(key, errors[key][0])
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal show={show} onClose={handleClose} size={"2xl"}>
      <Modal.Header>Edit Customer Details</Modal.Header>
      <Modal.Body>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({
            getFieldProps,
            errors,
            values,
            touched,
            isValid,
            resetForm,
            isSubmitting,
            setFieldValue,
          }) => (
            <Form>
              <div className={"p-6"}>
                <div className={"flex items-center justify-between gap-4"}>
                  <FormGroup
                    label={"First name"}
                    name={"first_name"}
                    requiredField
                    className={"flex-1"}
                  >
                    <TextInput
                      {...getFieldProps("first_name")}
                      placeholder={"Enter First Name"}
                      autoComplete={"off"}
                      className={"bg-white"}
                      color={errors.first_name && touched.first_name ? "failure" : undefined}
                    />
                  </FormGroup>
                  <FormGroup
                    label={"Last name"}
                    name={"last_name"}
                    requiredField
                    className={"flex-1"}
                  >
                    <TextInput
                      {...getFieldProps("last_name")}
                      placeholder={"Enter Last Name"}
                      autoComplete={"off"}
                      className={"bg-white"}
                      color={errors.last_name && touched.last_name ? "failure" : undefined}
                    />
                  </FormGroup>
                </div>
                <FormGroup label={"Email"} name={"email"} requiredField className={"mt-4"}>
                  <TextInput
                    {...getFieldProps("email")}
                    placeholder={"Enter Email"}
                    autoComplete={"off"}
                    className={"bg-white"}
                    color={errors.email && touched.email ? "failure" : undefined}
                  />
                </FormGroup>
                <FormGroup label={"Phone Number"} name={"phone"} requiredField className={"mt-4"}>
                  <PhoneInput
                    dialCodeName={"phone_code"}
                    name={"phone"}
                    onChange={(value, country) => setFieldValue("phone_code", country.dialCode)}
                  />
                </FormGroup>
              </div>
              <div
                className={`
                flex flex-1 flex-row items-center border-t border-neutral-200 bg-whiteBlack-100 px-6 py-4 justify-end
              `}
              >
                <div className={"flex flex-row items-center gap-3"}>
                  <Button
                    outline
                    className={"border-primary-600 text-primary-600"}
                    size={"md"}
                    onClick={() => {
                      resetForm()
                      handleClose()
                    }}
                  >
                    Cancel
                  </Button>
                  <Button size={"md"} type={"submit"} color={"primary"} disabled={!isValid}>
                    {isSubmitting ? <Spinner size={"sm"} light /> : <>Save</>}
                  </Button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  )
}

export default EditCustomerModal
